// posFullscreen.js — the POS's full-screen mode.
//
// A till screen is one screen: the menu, the cart and the buttons. Everything
// the app wraps around it — the top bar with the search, the bell and the
// speaker, the department sidebar, the Restaurant Orders tab strip — is for
// the office, and on a 14" laptop it costs the POS roughly a third of the
// window.
//
// Three components have to agree on the mode (the POS owns the button,
// AdminLayout owns the bar and the sidebar, RestaurantOrdersLayout owns the
// tab strip), so it lives here rather than in any one of them, and travels as
// a window event instead of props threaded through screens that do not care.
//
// The browser's own full screen is asked for on top of that - it is what
// removes the address bar and the taskbar - but it is only ever a bonus: a
// browser that refuses still gets the chrome-free POS.
import { useEffect, useState } from 'react';

export const POS_FULLSCREEN_EVENT = 'posFullscreenChange';

let active = false;
export const isPosFullscreen = () => active;

const askBrowser = (want) => {
  try {
    if (want && !document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else if (!want && document.fullscreenElement) {
      document.exitFullscreen?.();
    }
  } catch {
    /* refused (permissions, an iframe, an old browser) - the chrome still hides */
  }
};

export function setPosFullscreen(next) {
  active = !!next;
  askBrowser(active);
  try {
    window.dispatchEvent(
      new CustomEvent(POS_FULLSCREEN_EVENT, { detail: active }),
    );
  } catch {
    /* nothing listening */
  }
}

// Esc leaves the browser's full screen without telling the app. Without this
// the chrome would stay hidden and the user would be on a screen with no way
// back to the sidebar.
if (typeof document !== 'undefined') {
  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement && active) setPosFullscreen(false);
  });
}

/** Re-renders the component whenever the mode changes. */
export function usePosFullscreen() {
  const [value, setValue] = useState(active);
  useEffect(() => {
    const onChange = (e) => setValue(!!e.detail);
    window.addEventListener(POS_FULLSCREEN_EVENT, onChange);
    // A component mounting mid-session (the POS opening while the mode is
    // already on) starts from the truth, not from its initial state.
    setValue(active);
    return () => window.removeEventListener(POS_FULLSCREEN_EVENT, onChange);
  }, []);
  return value;
}
